import React, { useRef, useState } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Check,
  CheckCircle2,
  ChevronLeft,
  CircleArrowOutUpRight,
  Crown,
  Gamepad2,
  GraduationCap,
  Headphones,
  Loader2,
  MessageCircleMore,
  MonitorUp,
  Radio,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';

import ReCAPTCHA from 'react-google-recaptcha';

type FormStep = 1 | 2;
type SubmitStatus = 'idle' | 'submitting' | 'success' | 'error';

type ApplicationForm = {
  name: string;
  email: string;
  communityName: string;
  communityType: string;
  memberCount: string;
  currentPlatform: string;
  socialLink: string;
  expectation: string;
  consent: boolean;
};

/**
 * Backend hazır olduğunda buraya gerçek endpoint'i yaz:
 * Örnek: '/api/kurucu-topluluklar'
 * Boş bırakılırsa form TASARIM MODUNDA çalışır ve yalnızca başarı ekranını gösterir.
 */
 const FORM_ENDPOINT = 'https://sesgelio.com/api/founding-community-apply';

const initialFormData: ApplicationForm = {
  name: '',
  email: '',
  communityName: '',
  communityType: '',
  memberCount: '',
  currentPlatform: '',
  socialLink: '',
  expectation: '',
  consent: false,
};

const communities = [
  { icon: Gamepad2, title: 'Oyun toplulukları', code: '01' },
  { icon: Radio, title: 'Yayıncı toplulukları', code: '02' },
  { icon: Trophy, title: 'E-spor takımları', code: '03' },
  { icon: GraduationCap, title: 'Üniversite kulüpleri', code: '04' },
  { icon: MessageCircleMore, title: 'Sohbet ve müzik toplulukları', code: '05' },
  { icon: Users, title: 'Arkadaş grupları', code: '06' },
];

const benefits = [
  {
    icon: Crown,
    eyebrow: 'STATÜ',
    title: 'Kalıcı Kurucu Topluluk rozeti',
    description: 'Topluluğun, Sesgelio’nun ilk döneminden gelen özel kimliğini kalıcı olarak taşır.',
  },
  {
    icon: ShieldCheck,
    eyebrow: 'DESTEK',
    title: 'Doğrudan kurulum desteği',
    description: 'Kanallar, roller ve topluluk yapısı ihtiyaçlarına göre birlikte hazırlanır.',
  },
  {
    icon: Sparkles,
    eyebrow: 'ERKEN ERİŞİM',
    title: 'Yeni özelliklerde öncelik',
    description: 'Yeni araçları daha erken deneyip ürünün gelişiminde geri bildirim verebilirsin.',
  },
  {
    icon: BadgeCheck,
    eyebrow: 'GÖRÜNÜRLÜK',
    title: 'Sesgelio kanallarında tanıtım',
    description: 'Seçilen topluluklar, Sesgelio’nun sosyal medya ve topluluk içeriklerinde öne çıkarılır.',
  },
];

const programSteps = [
  ['01', 'Başvurunu gönder', 'Topluluğunu ve beklentilerini kısa formda paylaş.'],
  ['02', 'Birlikte değerlendirelim', 'Topluluk yapını ve ihtiyaçlarını doğrudan inceleyelim.'],
  ['03', 'Alanını hazırlayalım', 'Sunucunu, rollerini ve kanallarını birlikte kuralım.'],
  ['04', 'İlk üyelerini davet et', 'Topluluğunu taşı ve kurucu dönem ayrıcalıklarını kullan.'],
];

const faqs = [
  {
    question: 'Programa katılım ücretli mi?',
    answer: 'Hayır. Kurucu Topluluklar Programı’nın ilk dönem başvurusu ücretsizdir.',
  },
  {
    question: 'Her başvuru kabul ediliyor mu?',
    answer: 'Hayır. Topluluk yapısı, aktiflik ve programa uygunluk birlikte değerlendirilir.',
  },
  {
    question: 'Mevcut topluluğumu tamamen taşımam gerekir mi?',
    answer: 'Hayır. Önce küçük bir ekiple deneyebilir, ardından topluluğunu kademeli biçimde taşıyabilirsin.',
  },
  {
    question: 'Başvuru sonrasında ne olacak?',
    answer: 'Uygun bulunan topluluklarla iletişime geçilir ve kurulum süreci birlikte planlanır.',
  },
];

const KurucuTopluluklar: React.FC = () => {
  const pageScrollRef = useRef<HTMLDivElement>(null);
  const [formStep, setFormStep] = useState<FormStep>(1);
  const [formData, setFormData] = useState<ApplicationForm>(initialFormData);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');
  const [previewFailed, setPreviewFailed] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const scrollToForm = () => {
    const formSection = document.getElementById('basvuru');
    const scrollContainer = pageScrollRef.current;

    if (!formSection || !scrollContainer) return;

    const targetTop = formSection.offsetTop - 80;
    scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const target = event.target;
    const nextValue = target instanceof HTMLInputElement && target.type === 'checkbox'
      ? target.checked
      : target.value;

    setFormData((current) => ({
      ...current,
      [target.name]: nextValue,
    }));
  };

  const getAttribution = () => {
    if (typeof window === 'undefined') return {};

    const params = new URLSearchParams(window.location.search);
    return {
      utmSource: params.get('utm_source') ?? '',
      utmMedium: params.get('utm_medium') ?? '',
      utmCampaign: params.get('utm_campaign') ?? '',
      referrer: document.referrer,
      landingUrl: window.location.href,
    };
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError('');

    if (formStep === 1) {
      setFormStep(2);
      requestAnimationFrame(() => {
        const formCard = document.getElementById('basvuru-karti');
        const scrollContainer = pageScrollRef.current;

        if (!formCard || !scrollContainer) return;

        const targetTop = formCard.offsetTop - Math.max(24, (scrollContainer.clientHeight - formCard.clientHeight) / 2);
        scrollContainer.scrollTo({ top: targetTop, behavior: 'smooth' });
      });
      return;
    }

    if (!formData.consent) {
      setSubmitError('Başvuruyu göndermek için iletişim iznini onaylamalısın.');
      return;
    }

    if (!captchaToken) {
      setSubmitError('Lütfen robot olmadığınızı doğrulayın.');
      return;
    }

    setSubmitStatus('submitting');

    try {
      if (FORM_ENDPOINT) {
        const response = await fetch(FORM_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({
            ...formData,
            captchaToken,
            ...getAttribution(),
            submittedAt: new Date().toISOString(),
          }),
        });

if (!response.ok) {
  const rawText = await response.text();
  console.error('Sunucu cevabı (status ' + response.status + '):', rawText);

  let errorMessage = 'Başvuru sunucuya kaydedilemedi.';
  try {
    const errorData = JSON.parse(rawText);
    if (errorData.error) errorMessage = errorData.error;
  } catch {
    // JSON değilse (HTML 404/502 sayfası gibi) durumu açıkça belirt
    errorMessage = `Sunucu beklenmeyen bir cevap döndü (HTTP ${response.status}). Lütfen daha sonra tekrar deneyin.`;
  }
  throw new Error(errorMessage);
}
      } else {
        // Tasarım modu: Backend bağlanana kadar kullanıcı akışını test etmek için.
        console.info('Kurucu Topluluk başvurusu (tasarım modu):', {
          ...formData,
          ...getAttribution(),
        });
      }

      setSubmitStatus('success');
      requestAnimationFrame(() => pageScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' }));
    } catch (error) {
      setSubmitStatus('error');
      setSubmitError(error instanceof Error ? error.message : 'Beklenmeyen bir hata oluştu.');
    }
  };

  if (submitStatus === 'success') {
    return (
      <main className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-[#0a0908] px-5 py-12 text-[#f4efe8]">
        <div
          className="pointer-events-none absolute inset-0 opacity-70"
          style={{
            backgroundImage:
              'radial-gradient(circle at 50% 30%, rgba(255,39,79,0.20), transparent 34%), linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
            backgroundSize: 'auto, 56px 56px, 56px 56px',
          }}
        />

        <section className="relative z-10 w-full max-w-xl border border-white/10 bg-[#11100e]/95 p-6 shadow-[0_40px_120px_rgba(0,0,0,0.55)] sm:p-10">
          <div className="mb-10 flex items-start justify-between border-b border-white/10 pb-6">
            <div>
              <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.32em] text-[#d9b36c]">
                Kurucu Erişim
              </p>
              <p className="text-sm text-white/45">Başvuru kaydı / Sesgelio</p>
            </div>
            <span className="font-mono text-xs text-white/30">STATUS: RECEIVED</span>
          </div>

          <div className="mb-7 flex h-16 w-16 items-center justify-center rounded-full border border-emerald-400/25 bg-emerald-400/10 text-emerald-300">
            <CheckCircle2 size={32} />
          </div>

          <h1 className="max-w-md text-4xl font-semibold leading-[1.05] tracking-[-0.04em] sm:text-5xl">
            Başvurun bize ulaştı.
          </h1>
          <p className="mt-5 max-w-lg text-base leading-7 text-white/55">
            Topluluğunu inceleyip uygun sonraki adımları seninle paylaşacağız. Bu sırada hesabını oluşturarak Sesgelio’yu keşfedebilirsin.
          </p>

          <button
            type="button"
            onClick={() => { window.location.href = '/'; }}
            className="mt-9 inline-flex w-full items-center justify-between bg-[#ff274f] px-5 py-4 text-sm font-bold text-white transition hover:bg-[#ff4166] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6b85] sm:w-auto sm:min-w-72"
          >
            Ücretsiz hesap oluştur
            <ArrowRight size={18} />
          </button>
        </section>
      </main>
    );
  }

  return (
    <div
      ref={pageScrollRef}
      className="h-[100dvh] w-full overflow-y-auto overflow-x-hidden overscroll-y-contain bg-[#0a0908] text-[#f4efe8] selection:bg-[#ff274f] selection:text-white"
      style={{ WebkitOverflowScrolling: 'touch' }}
    >
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0a0908]/90 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-[1440px] items-center justify-between px-5 sm:px-8 lg:px-12">
          <button
            type="button"
            onClick={() => { window.location.href = '/'; }}
            className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff274f]"
            aria-label="Sesgelio ana sayfasına dön"
          >
<div className="relative h-14 w-[170px] shrink-0 overflow-hidden">
  <img
    src="/ana1_sayfa_logo.png"
    alt="Sesgelio"
    className="absolute left-1/2 top-1/2 h-auto w-full max-w-none origin-center scale-[1.1] -translate-x-1/2 -translate-y-1/2 object-contain"
  />
</div>
          </button>

          <div className="hidden items-center gap-8 md:flex">
            <a href="#ayricaliklar" className="text-sm text-white/55 transition hover:text-white">Ayrıcalıklar</a>
            <a href="#surec" className="text-sm text-white/55 transition hover:text-white">Süreç</a>
            <button
              type="button"
              onClick={scrollToForm}
              className="inline-flex items-center gap-2 border border-white/15 px-4 py-2.5 text-sm font-semibold transition hover:border-white/35 hover:bg-white/5"
            >
              Başvuruya geç
              <ArrowRight size={16} />
            </button>
          </div>

          <button
            type="button"
            onClick={scrollToForm}
            className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 text-xs font-semibold md:hidden"
          >
            Başvur
            <ArrowRight size={14} />
          </button>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div
            className="pointer-events-none absolute inset-0 opacity-60"
            style={{
              backgroundImage:
                'radial-gradient(circle at 12% 10%, rgba(255,39,79,0.18), transparent 32%), radial-gradient(circle at 88% 65%, rgba(217,179,108,0.10), transparent 28%), linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
              backgroundSize: 'auto, auto, 72px 72px, 72px 72px',
            }}
          />

          <div className="relative mx-auto grid min-h-[calc(100dvh-80px)] max-w-[1440px] items-center gap-16 px-5 py-20 sm:px-8 lg:grid-cols-[0.92fr_1.08fr] lg:px-12 lg:py-28">
            <div className="max-w-2xl">
              <div className="mb-10 flex items-center gap-4">
                <span className="font-mono text-xs text-[#d9b36c]">FOUNDING ACCESS / 01</span>
                <span className="h-px w-16 bg-[#d9b36c]/45" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.24em] text-white/45">
                  İlk dönem başvuruları açık
                </span>
              </div>

              <h1 className="text-[clamp(3.2rem,7vw,7.5rem)] font-semibold leading-[0.88] tracking-[-0.07em]">
                Sadece katılma.
                <span className="mt-4 block font-serif italic text-[#ff3b5f]">İlklerden ol.</span>
              </h1>

              <p className="mt-9 max-w-xl text-lg leading-8 text-white/58 sm:text-xl">
                Topluluğunu Sesgelio’nun kuruluş dönemine taşı. Özel statü, doğrudan destek ve ürünün gelişiminde söz sahibi olma ayrıcalığı kazan.
              </p>

              <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={scrollToForm}
                  className="group inline-flex items-center justify-between bg-[#ff274f] px-5 py-[18px] text-sm font-bold text-white transition hover:bg-[#ff4166] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ff6b85] sm:min-w-64"
                >
                  Topluluğumla başvur
                  <ArrowRight className="transition-transform group-hover:translate-x-1" size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => { window.location.href = '/'; }}
                  className="inline-flex items-center justify-between border border-white/15 px-5 py-[18px] text-sm font-semibold text-white/75 transition hover:border-white/35 hover:bg-white/5 hover:text-white sm:min-w-52"
                >
                  Sesgelio’yu keşfet
                  <CircleArrowOutUpRight size={18} />
                </button>
              </div>

              <div className="mt-12 grid max-w-xl grid-cols-3 border-y border-white/10 py-5">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">Program</p>
                  <p className="mt-2 text-sm font-semibold">Kurucu dönem</p>
                </div>
                <div className="border-x border-white/10 px-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">Erişim</p>
                  <p className="mt-2 text-sm font-semibold">Başvuru ile</p>
                </div>
                <div className="pl-4">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/30">Ücret</p>
                  <p className="mt-2 text-sm font-semibold">Ücretsiz</p>
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-3xl lg:mx-0">
              <div className="absolute -inset-4 border border-[#ff274f]/15" />
              <div className="relative overflow-hidden border border-white/15 bg-[#10100f] shadow-[0_50px_140px_rgba(0,0,0,0.60)]">
                <div className="flex h-12 items-center justify-between border-b border-white/10 px-4">
                  <div className="flex gap-2">
                    <span className="h-2 w-2 rounded-full bg-white/15" />
                    <span className="h-2 w-2 rounded-full bg-white/15" />
                    <span className="h-2 w-2 rounded-full bg-[#ff274f]" />
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.18em] text-white/30">SESGELIO / LIVE PREVIEW</span>
                </div>

                <div className="relative aspect-[16/10] overflow-hidden bg-[#0d0d0c]">
                  {!previewFailed ? (
                    <img
                      src="/kurucu_topluluklar_hero.png"
                      alt="Sesgelio uygulama arayüzü"
                      className="h-full w-full object-cover object-left-top"
                      onError={() => setPreviewFailed(true)}
                    />
                  ) : (
                    <div className="absolute inset-0 grid grid-cols-[80px_1fr] sm:grid-cols-[96px_1fr]">
                      <aside className="border-r border-white/10 bg-[#0b0b0a] p-3">
                        <div className="mb-5 h-10 w-10 rounded-xl bg-[#ff274f]" />
                        <div className="space-y-3">
                          {[0, 1, 2, 3, 4].map((item) => (
                            <div key={item} className="h-9 rounded-lg border border-white/5 bg-white/[0.03]" />
                          ))}
                        </div>
                      </aside>
                      <div className="p-4 sm:p-6">
                        <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-4">
                          <div>
                            <p className="text-xs font-semibold text-white/70">Kurucu Topluluk</p>
                            <p className="mt-1 text-[10px] text-white/30">Sesli odalar ve topluluk alanı</p>
                          </div>
                          <span className="rounded-full bg-emerald-400/10 px-3 py-1 text-[10px] text-emerald-300">Çevrimiçi</span>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                          {[['Oyun Odası', '12 aktif'], ['Topluluk Sahnesi', '8 aktif'], ['Yayın Ekibi', '5 aktif'], ['Kurucu Lounge', '3 aktif']].map(([title, meta]) => (
                            <div key={title} className="border border-white/10 bg-white/[0.025] p-4">
                              <Headphones size={18} className="mb-5 text-[#ff3b5f]" />
                              <p className="text-xs font-semibold text-white/80">{title}</p>
                              <p className="mt-1 text-[10px] text-white/30">{meta}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between bg-gradient-to-t from-black/85 via-black/25 to-transparent p-4 pt-20 sm:p-6">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#d9b36c]">KURUCU STATÜSÜ</p>
                      <p className="mt-2 max-w-sm text-sm text-white/60">Topluluğunuza ilk günden görünür bir prestij kazandırın.</p>
                    </div>
                    <MonitorUp className="hidden text-white/30 sm:block" size={24} />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-5 -right-3 border border-[#d9b36c]/25 bg-[#11100e] px-4 py-3 shadow-xl sm:-right-6">
                <p className="font-mono text-[10px] tracking-[0.16em] text-[#d9b36c]">FOUNDING COMMUNITY</p>
                <p className="mt-1 text-xs text-white/55">Kuruluş döneminin parçası</p>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-white/10 bg-[#f0ebe3] text-[#151310]">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:px-12 lg:py-28">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#a11b35]">Kurucu manifesto</p>
              <h2 className="mt-5 max-w-md text-4xl font-semibold leading-[0.98] tracking-[-0.045em] sm:text-6xl">
                Bir platforma değil, bir döneme katılıyorsun.
              </h2>
            </div>

            <div className="grid gap-px border border-black/15 bg-black/15 sm:grid-cols-2">
              {[
                ['01', 'Topluluğuna ait alan', 'Kanallarını, rollerini ve iletişim düzenini kendi kültürüne göre kur.'],
                ['02', 'Düşük gecikmeli ses', 'Oyun ve sohbet akışını bozmayan yüksek kaliteli ses deneyimi.'],
                ['03', '2K 60 FPS’e kadar paylaşım', 'Uygun paketle ekranını daha akıcı ve net biçimde paylaş.'],
                ['04', 'Gelişimde söz hakkı', 'Erken dönem geri bildirimlerin ürün kararlarında doğrudan değerlendirilir.'],
              ].map(([number, title, description]) => (
                <article key={number} className="bg-[#f0ebe3] p-6 sm:p-8">
                  <p className="font-mono text-xs text-[#a11b35]">{number}</p>
                  <h3 className="mt-10 text-xl font-semibold tracking-[-0.02em]">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-black/55">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-white/10" aria-labelledby="kimler-katilabilir">
          <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
            <div className="mb-12 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#d9b36c]">Topluluk profilleri</p>
                <h2 id="kimler-katilabilir" className="mt-4 text-4xl font-semibold tracking-[-0.04em] sm:text-6xl">
                  Kimler katılabilir?
                </h2>
              </div>
              <p className="max-w-md text-sm leading-6 text-white/45">
                Aktif bir kültürü, amacı veya ortak ilgisi bulunan bütün topluluklar başvuru yapabilir.
              </p>
            </div>

            <div className="grid border-l border-t border-white/10 sm:grid-cols-2 lg:grid-cols-3">
              {communities.map(({ icon: Icon, title, code }) => (
                <article
                  key={code}
                  className="group min-h-52 border-b border-r border-white/10 p-6 transition hover:bg-white/[0.035] sm:p-8"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={24} className="text-[#ff3b5f]" />
                    <span className="font-mono text-xs text-white/25">{code}</span>
                  </div>
                  <h3 className="mt-20 max-w-xs text-xl font-semibold tracking-[-0.025em] transition group-hover:translate-x-1">
                    {title}
                  </h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="ayricaliklar" className="scroll-mt-24 border-b border-white/10">
          <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
            <div className="grid gap-16 lg:grid-cols-[0.78fr_1.22fr]">
              <div className="lg:sticky lg:top-28 lg:self-start">
                <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#d9b36c]">Program ayrıcalıkları</p>
                <h2 className="mt-5 text-4xl font-semibold leading-[0.98] tracking-[-0.045em] sm:text-6xl">
                  İlk olmak görünür bir fark yaratır.
                </h2>
                <p className="mt-6 max-w-md text-base leading-7 text-white/48">
                  Program, yalnızca özellik sunmaz. Topluluğuna kuruluş dönemine ait kalıcı bir kimlik ve doğrudan ilişki sağlar.
                </p>
              </div>

              <div className="border-t border-white/10">
                {benefits.map(({ icon: Icon, eyebrow, title, description }, index) => (
                  <article key={title} className="grid gap-6 border-b border-white/10 py-8 sm:grid-cols-[64px_1fr_auto] sm:items-start sm:py-10">
                    <div className="flex h-12 w-12 items-center justify-center border border-white/12 text-[#ff3b5f]">
                      <Icon size={22} />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.2em] text-[#d9b36c]">{eyebrow}</p>
                      <h3 className="mt-3 text-2xl font-semibold tracking-[-0.025em]">{title}</h3>
                      <p className="mt-3 max-w-xl text-sm leading-6 text-white/45">{description}</p>
                    </div>
                    <span className="hidden font-mono text-xs text-white/20 sm:block">0{index + 1}</span>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="surec" className="scroll-mt-24 border-b border-white/10 bg-[#11100e]">
          <div className="mx-auto max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12 lg:py-28">
            <div className="mb-14 max-w-3xl">
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#d9b36c]">Program süreci</p>
              <h2 className="mt-5 text-4xl font-semibold tracking-[-0.045em] sm:text-6xl">
                Dört adımda topluluğun Sesgelio’da.
              </h2>
            </div>

            <div className="grid border-l border-t border-white/10 lg:grid-cols-4">
              {programSteps.map(([number, title, description]) => (
                <article key={number} className="min-h-72 border-b border-r border-white/10 p-6 sm:p-8">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-[#ff3b5f]">{number}</span>
                    <ArrowRight size={16} className="text-white/20" />
                  </div>
                  <h3 className="mt-24 text-xl font-semibold tracking-[-0.025em]">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-white/42">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="basvuru" className="scroll-mt-20 bg-[#f0ebe3] text-[#151310]">
          <div className="mx-auto grid max-w-[1440px] gap-16 px-5 py-20 sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:px-12 lg:py-28">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#a11b35]">Kurucu erişim başvurusu</p>
              <h2 className="mt-5 max-w-xl text-4xl font-semibold leading-[0.98] tracking-[-0.045em] sm:text-6xl">
                Topluluğunun yerini ayır.
              </h2>
              <p className="mt-6 max-w-md text-base leading-7 text-black/55">
                Başvurunu tamamla. Topluluğunun yapısını birlikte inceleyip uygun geçiş planını oluşturalım.
              </p>

              <div className="mt-10 space-y-4 border-t border-black/15 pt-6 text-sm text-black/55">
                <p className="flex items-center gap-3"><Check size={16} className="text-[#a11b35]" /> İlk dönem başvurusu ücretsizdir.</p>
                <p className="flex items-center gap-3"><Check size={16} className="text-[#a11b35]" /> Bilgilerin yalnızca başvuru için kullanılır.</p>
                <p className="flex items-center gap-3"><Check size={16} className="text-[#a11b35]" /> Uygun topluluklarla doğrudan iletişime geçilir.</p>
              </div>
            </div>

            <form
              id="basvuru-karti"
              onSubmit={handleSubmit}
              className="border border-black/15 bg-[#f7f3ed] p-5 shadow-[0_36px_100px_rgba(42,28,18,0.12)] sm:p-8 lg:p-10"
            >
              <div className="mb-8 flex items-start justify-between gap-6 border-b border-black/12 pb-6">
                <div>
                  <p className="text-sm font-semibold">Başvuru formu</p>
                  <p className="mt-1 text-xs text-black/40">Yaklaşık 2 dakika sürer.</p>
                </div>
                <div className="text-right">
                  <p className="font-mono text-xs text-[#a11b35]">0{formStep} / 02</p>
                  <div className="mt-2 flex gap-1">
                    <span className="h-1 w-8 bg-[#a11b35]" />
                    <span className={`h-1 w-8 ${formStep === 2 ? 'bg-[#a11b35]' : 'bg-black/12'}`} />
                  </div>
                </div>
              </div>

              {formStep === 1 ? (
                <div className="space-y-5">
                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Ad soyad</span>
                    <input
                      required
                      autoComplete="name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Adınız Soyadınız"
                      className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">E-posta adresi</span>
                    <input
                      required
                      autoComplete="email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="iletisim@ornek.com"
                      className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Topluluk adı</span>
                    <input
                      required
                      type="text"
                      name="communityName"
                      value={formData.communityName}
                      onChange={handleInputChange}
                      placeholder="Örn. X E-spor Topluluğu"
                      className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                  </label>

                  <button
                    type="submit"
                    className="group mt-3 inline-flex w-full items-center justify-between bg-[#151310] px-5 py-[18px] text-sm font-bold text-white transition hover:bg-[#2a2520] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a11b35]"
                  >
                    Sonraki adıma geç
                    <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                  </button>
                </div>
              ) : (
                <div className="space-y-5">
                  <div className="grid gap-5 sm:grid-cols-2">
                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Topluluk türü</span>
                      <select
                        required
                        name="communityType"
                        value={formData.communityType}
                        onChange={handleInputChange}
                        className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                      >
                        <option value="">Seçiniz</option>
                        <option value="Oyun / E-spor">Oyun / E-spor</option>
                        <option value="Yayıncı topluluğu">Yayıncı topluluğu</option>
                        <option value="Üniversite / Eğitim">Üniversite / Eğitim</option>
                        <option value="Müzik / Sohbet">Müzik / Sohbet</option>
                        <option value="Arkadaş grubu">Arkadaş grubu</option>
                        <option value="Diğer">Diğer</option>
                      </select>
                    </label>

                    <label className="block">
                      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Yaklaşık üye sayısı</span>
                      <select
                        required
                        name="memberCount"
                        value={formData.memberCount}
                        onChange={handleInputChange}
                        className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                      >
                        <option value="">Seçiniz</option>
                        <option value="1-25">1–25</option>
                        <option value="26-100">26–100</option>
                        <option value="101-500">101–500</option>
                        <option value="501-1.000">501–1.000</option>
                        <option value="1.000+">1.000+</option>
                      </select>
                    </label>
                  </div>

                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Şu an kullanılan platform</span>
                    <input
                      required
                      type="text"
                      name="currentPlatform"
                      value={formData.currentPlatform}
                      onChange={handleInputChange}
                      placeholder="Örn. Discord, TeamSpeak, WhatsApp"
                      className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Sosyal medya veya topluluk bağlantısı</span>
                    <input
                      type="url"
                      name="socialLink"
                      value={formData.socialLink}
                      onChange={handleInputChange}
                      placeholder="https://"
                      className="w-full border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.14em] text-black/45">Sesgelio’dan beklentin</span>
                    <textarea
                      name="expectation"
                      value={formData.expectation}
                      onChange={handleInputChange}
                      rows={4}
                      maxLength={700}
                      placeholder="Topluluğun için nasıl bir deneyim aradığını kısaca anlat."
                      className="w-full resize-none border border-black/15 bg-transparent px-4 py-4 text-sm outline-none transition placeholder:text-black/28 focus:border-[#a11b35] focus:ring-1 focus:ring-[#a11b35]"
                    />
                    <span className="mt-2 block text-right font-mono text-[10px] text-black/35">{formData.expectation.length}/700</span>
                  </label>

                  <label className="flex cursor-pointer items-start gap-3 border border-black/12 p-4">
                    <input
                      required
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={handleInputChange}
                      className="mt-0.5 h-4 w-4 accent-[#a11b35]"
                    />
                    <span className="text-xs leading-5 text-black/52">
                      İletişim bilgilerimin başvuru sürecinin yürütülmesi amacıyla Sesgelio tarafından kullanılmasına izin veriyorum.
                    </span>
                  </label>

                  {/* --- YENİ EKLENEN RECAPTCHA GÜVENLİK ALANI --- */}
                  <div className="mt-4 flex justify-start">
                    <ReCAPTCHA
                      sitekey="6LfvN0gsAAAAAJP8YUs4MEsSzCc06dKB66HqugK2"
                      onChange={(token) => setCaptchaToken(token)}
                      theme="light"
                    />
                  </div>

                  {submitError && (
                    <p role="alert" className="border border-red-500/25 bg-red-500/5 px-4 py-3 text-sm text-red-700">
                      {submitError}
                    </p>
                  )}

                  <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                    <button
                      type="button"
                      onClick={() => setFormStep(1)}
                      disabled={submitStatus === 'submitting'}
                      className="inline-flex items-center justify-center gap-2 border border-black/15 px-5 py-[18px] text-sm font-semibold transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-50 sm:w-36"
                    >
                      <ChevronLeft size={17} />
                      Geri
                    </button>
                    <button
                      type="submit"
                      disabled={submitStatus === 'submitting'}
                      className="group inline-flex flex-1 items-center justify-between bg-[#a11b35] px-5 py-[18px] text-sm font-bold text-white transition hover:bg-[#b92240] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#a11b35] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {submitStatus === 'submitting' ? (
                        <>
                          Başvuru gönderiliyor
                          <Loader2 size={18} className="animate-spin" />
                        </>
                      ) : (
                        <>
                          Başvuruyu tamamla
                          <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>
        </section>

        <section className="border-t border-white/10">
          <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 sm:px-8 lg:grid-cols-[0.72fr_1.28fr] lg:px-12 lg:py-24">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#d9b36c]">Sık sorulanlar</p>
              <h2 className="mt-5 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">Karar vermeden önce.</h2>
            </div>

            <div className="border-t border-white/10">
              {faqs.map(({ question, answer }) => (
                <details key={question} className="group border-b border-white/10 py-6">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-base font-semibold">
                    {question}
                    <span className="text-[#ff3b5f] transition-transform group-open:rotate-45">+</span>
                  </summary>
                  <p className="max-w-2xl pt-4 text-sm leading-6 text-white/45">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-5 px-5 py-8 text-xs text-white/32 sm:flex-row sm:items-center sm:justify-between sm:px-8 lg:px-12">
          <p>© 2026 Sesgelio. Tüm hakları saklıdır.</p>
          <div className="flex gap-5">
            <a href="/gizlilik" className="transition hover:text-white">Gizlilik</a>
            <a href="/kullanim-kosullari" className="transition hover:text-white">Kullanım koşulları</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default KurucuTopluluklar;